import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import Button from "./common/Button";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  ArrowLeft,
  Clock,
  Users,
  Stethoscope,
  Loader2,
  UserPlus,
  CheckCircle,
  Send,
} from "lucide-react";
import config from "../config";
import { getOrganizationDisplayName } from "../utils/utils";
import { formatDateDMY } from "../lib/ScheduleUtils";
import Spinner from "./common/Spinner";
import * as utils from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { apiJsonHeaders, withApiLocale } from "../utils/apiLocale";

export default function AssociationLanding() {
  const { t } = useBrandTranslation();
  const { associationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [association, setAssociation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");

  useEffect(() => {
    if (associationId) {
      loadAssociationData();
    }
  }, [associationId, user]);

  const loadAssociationData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos de la asociación
      const { data: associationData, error: associationError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", associationId)
        .eq("role", "association")
        .maybeSingle();

      if (associationError) throw associationError;

      if (!associationData) {
        setError("Asociación no encontrada");
        setLoading(false);
        return;
      }

      setAssociation(associationData);

      // Cargar profesionales suscritos
      await loadSubscribers(associationData.id);

      // Verificar si el usuario actual está suscrito
      if (user && user.role === "doctor") {
        await checkSubscription(associationData.id, user.id);
      }
    } catch (err) {
      console.error("Error loading association:", err);
      setError("Error al cargar los datos de la asociación");
    } finally {
      setLoading(false);
    }
  };

  const loadSubscribers = async (associationId) => {
    try {
      const response = await fetch(
        `${config.baseUrlApi}/associations/${associationId}/subscribers`,
        { headers: apiJsonHeaders() }
      );
      if (!response.ok) throw new Error("Error al cargar suscriptores");
      const result = await response.json();
      setProfessionals(result.data || []);
    } catch (err) {
      console.error("Error loading subscribers:", err);
      setProfessionals([]);
    }
  };

  const checkSubscription = async (associationId, professionalId) => {
    try {
      const response = await fetch(
        `${config.baseUrlApi}/professionals/${professionalId}/association`,
        { headers: apiJsonHeaders() }
      );
      if (!response.ok) {
        setIsSubscribed(false);
        return;
      }
      const result = await response.json();
      setIsSubscribed(result && result.association_id === associationId);
    } catch (err) {
      console.error("Error checking subscription:", err);
      setIsSubscribed(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError("");
    setInviteSuccess("");

    if (!inviteEmail.trim()) {
      setInviteError("El email es obligatorio");
      return;
    }

    if (!inviteName.trim() || !inviteLastName.trim()) {
      setInviteError("Nombre y apellido son obligatorios");
      return;
    }

    try {
      setIsInviting(true);
      const response = await fetch(
        `${config.baseUrlApi}/associations/${associationId}/invite`,
        {
          method: "POST",
          headers: apiJsonHeaders(),
          body: JSON.stringify(
            withApiLocale({
              email: inviteEmail.trim(),
              name: inviteName.trim(),
              last_name: inviteLastName.trim(),
              phone: invitePhone.trim() || undefined,
            })
          ),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Error al enviar invitación");
      }

      setInviteSuccess("Invitación enviada exitosamente");
      setInviteEmail("");
      setInviteName("");
      setInviteLastName("");
      setInvitePhone("");
      setShowInviteForm(false);
      
      // Recargar suscriptores
      await loadSubscribers(associationId);
    } catch (err) {
      console.error("Error inviting:", err);
      setInviteError(err.message || "Error al enviar invitación");
    } finally {
      setIsInviting(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user || user.role !== "doctor") {
      setSubscriptionError("Debes ser un profesional para suscribirte");
      return;
    }

    try {
      setIsSubscribing(true);
      setSubscriptionError("");

      const response = await fetch(
        `${config.baseUrlApi}/associations/${associationId}/subscribe`,
        {
          method: "POST",
          headers: apiJsonHeaders(),
          body: JSON.stringify(
            withApiLocale({
              professional_id: user.id,
            })
          ),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Error al suscribirse");
      }

      setIsSubscribed(true);
      await loadSubscribers(associationId);
    } catch (err) {
      console.error("Error subscribing:", err);
      setSubscriptionError(err.message || "Error al suscribirse");
    } finally {
      setIsSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t("app.landing.loadingAssociation", "Cargando información de la asociación...")}</p>
        </div>
      </div>
    );
  }

  if (error || !association) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.commonUi.error", "Error")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              {error || "Asociación no encontrada"}
            </p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const associationName = getOrganizationDisplayName(association);
  const showSubscribeButton = user && user.role === "doctor" && !isSubscribed;
  const canInvite = user && user.role === "association" && user.id === associationId;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header con botón volver */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>

        {/* Hero Section */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              {association.picture ? (
                <img
                  src={association.picture}
                  alt={associationName}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <Building2 className="w-16 h-16" />
              )}
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-2">{associationName}</h1>
                <p className="text-purple-100 text-lg">{t("app.landing.professionalAssociation", "Asociación Profesional")}</p>
              
              <div className="flex gap-2 mt-4">
                {showSubscribeButton && (
                  <Button
                    onClick={handleSubscribe}
                    disabled={isSubscribing}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    {isSubscribing ? (
                      <>
                        <Spinner className="w-4 h-4 mr-2" />
                        Suscribiendo...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Novedades
                      </>
                    )}
                  </Button>
                )}
                {canInvite && (
                  <Button
                    onClick={() => setShowInviteForm(!showInviteForm)}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invitar Profesional
                  </Button>
                )}
              </div>
              </div>
              {isSubscribed && (
                <div>
                  <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    <span>{t("app.landing.subscribed", "Suscrito")}</span>
                  </div>
                </div>
              )}
            </div>
            {subscriptionError && (
              <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-800 text-sm">
                {subscriptionError}
              </div>
            )}
          </div>
        </Card>

        {/* Información de contacto */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-purple-600" />
                Contacto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {association.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.profile.phone", "Teléfono")}</p>
                    <a
                      href={`tel:${association.phone}`}
                      className="text-purple-600 hover:text-purple-800 font-medium"
                    >
                      {association.phone}
                    </a>
                  </div>
                </div>
              )}

              {association.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.profile.email", "Email")}</p>
                    <a
                      href={`mailto:${association.email}`}
                      className="text-purple-600 hover:text-purple-800 font-medium break-all"
                    >
                      {association.email}
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-purple-600" />
                Información
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">{t("app.landing.memberSince", "Miembro desde")}</p>
                  <p className="text-gray-900 font-medium">
                    {formatDateDMY(association.created_at)}
                  </p>
                </div>
              </div>

              {professionals.length > 0 && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.landing.subscribedProfessionals", "Profesionales suscritos")}</p>
                    <p className="text-gray-900 font-medium">{professionals.length}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Descripción de la asociación */}
        {association?.description?.trim() && (
          <div className="mb-6">
            <div className="rounded-lg border bg-white border-gray-200 p-4 sm:p-6">
              <p className="text-gray-700 whitespace-pre-wrap">
                {association.description.trim()}
              </p>
            </div>
          </div>
        )}

        {/* Formulario de invitación */}
        {canInvite && showInviteForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-600" />
                Invitar Profesional
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="w-full rounded border border-slate-200 p-3 text-slate-700"
                      required
                      disabled={isInviting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellido *
                    </label>
                    <input
                      type="text"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      className="w-full rounded border border-slate-200 p-3 text-slate-700"
                      required
                      disabled={isInviting}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full rounded border border-slate-200 p-3 text-slate-700"
                      required
                      disabled={isInviting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono (opcional)
                    </label>
                    <input
                      type="text"
                      value={invitePhone}
                      onChange={(e) => setInvitePhone(e.target.value)}
                      className="w-full rounded border border-slate-200 p-3 text-slate-700"
                      disabled={isInviting}
                    />
                  </div>
                </div>
                {inviteError && (
                  <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    {inviteError}
                  </div>
                )}
                {inviteSuccess && (
                  <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                    {inviteSuccess}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowInviteForm(false);
                      setInviteError("");
                      setInviteSuccess("");
                      setInviteEmail("");
                      setInviteName("");
                      setInviteLastName("");
                      setInvitePhone("");
                    }}
                    disabled={isInviting}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isInviting}>
                    {isInviting ? (
                      <>
                        <Spinner className="w-4 h-4 mr-2" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Invitación
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Profesionales suscritos */}
        {professionals.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-purple-600" />
                Profesionales Suscritos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {professionals.map((item) => {
                  const professional = item.professional;
                  if (!professional) return null;
                  return (
                    <div
                      key={item.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="font-semibold text-lg mb-1">
                        Dr. {professional.name} {professional.last_name}
                      </h3>
                      {professional.specialization && (
                        <p className="text-sm text-gray-600 mb-2">
                          {professional.specialization}
                        </p>
                      )}
                      {professional.email && (
                        <p className="text-xs text-gray-500">{professional.email}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Acciones */}
        {/* <div className="mt-6 flex gap-4 justify-center">
          <Button
            onClick={() => navigate("/patient/search")}
            className="flex items-center gap-2"
          >
            <Stethoscope className="w-4 h-4" />
            Buscar Profesionales
          </Button>
        </div> */}
      </div>
    </div>
  );
}

